import { memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Slider, { Settings } from 'react-slick'

export interface UsageScene {
  type: 'carousel' | 'single'
  images: string[]
  titleKey: string
  descriptionKey: string
  imageClassName?: string
}

const SliderComponent = Slider as unknown as React.ComponentClass<Settings>

function CarouselImages({ images }: { images: string[] }) {
  const carouselSettings: Settings = useMemo(
    () => ({
      dots: true,
      infinite: true,
      slidesToShow: 1,
      slidesToScroll: 1,
      autoplay: true,
      speed: 500,
      autoplaySpeed: 3000,
      cssEase: 'ease',
      arrows: false,
      swipeToSlide: true,
      appendDots: (dots: JSX.Element) => (
        <div>
          <ul className='m-0 flex list-none justify-center gap-2 p-0'>{dots}</ul>
        </div>
      ),
      customPaging: () => (
        <div className='h-2 w-2 rounded-full bg-gray-300 transition-all duration-200' />
      ),
    }),
    [],
  )

  return (
    <div className='relative rounded-lg'>
      <style>{`
        .slick-slider {
          position: relative;
        }
        .slick-dots {
          position: absolute !important;
          bottom: 14px !important;
          left: 0 !important;
          right: 0 !important;
          z-index: 10 !important;
        }
      `}</style>
      <SliderComponent {...carouselSettings}>
        {images.map((image, idx) => (
          <div key={idx}>
            <img
              src={image}
              alt=''
              className='w-full rounded-lg'
              loading={idx === 0 ? 'eager' : 'lazy'}
            />
          </div>
        ))}
      </SliderComponent>
    </div>
  )
}

const SceneImage = memo(function SceneImage({
  src,
  alt,
  className = 'w-full rounded-lg',
}: {
  src: string
  alt: string
  className?: string
}) {
  return <img src={src} alt={alt} className={className} loading='lazy' />
})

const SceneContent = memo(function SceneContent({
  title,
  description,
}: {
  title: string
  description: string
}) {
  return (
    <div className='space-y-4 text-center'>
      <h3 className='text-gray-12 tracking-0 text-xl font-semibold leading-[140%]'>{title}</h3>
      <div className='localized-body-copy text-gray-12 tracking-0 text-base font-normal leading-[160%]'>
        <p className='whitespace-pre-line'>{description}</p>
      </div>
    </div>
  )
})

interface UsageScenesSectionProps {
  contentClassName?: string
  sceneClassName?: string
  sceneContentWrapperClassName?: string
  scenes: UsageScene[]
  sectionClassName: string
  showHeading: boolean
}

export default function UsageScenesSection({
  contentClassName,
  sceneClassName,
  sceneContentWrapperClassName,
  scenes,
  sectionClassName,
  showHeading,
}: UsageScenesSectionProps) {
  const { t } = useTranslation('landingPage')

  return (
    <section className={sectionClassName}>
      <div className={contentClassName}>
        {showHeading && (
          <h2 className='text-gray-12 text-center text-2xl font-semibold'>
            {t('usageScenes.heading')}
          </h2>
        )}

        {scenes.map((scene, idx) => (
          <div key={idx} className={sceneClassName}>
            {scene.type === 'carousel' ? (
              <CarouselImages images={scene.images} />
            ) : (
              <SceneImage
                src={scene.images[0]}
                alt={t(scene.titleKey)}
                className={scene.imageClassName}
              />
            )}
            <div className={sceneContentWrapperClassName}>
              <SceneContent title={t(scene.titleKey)} description={t(scene.descriptionKey)} />
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}
